import React, { useMemo } from 'react';
import { ScrollView, Linking, ActivityIndicator } from 'react-native';
import { View } from 'react-native';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { Text } from '@/components/gluestack/text';
import { VStack } from '@/components/gluestack/vstack';
import { Card } from '@/components/gluestack/card';
import { Heading } from '@/components/gluestack/heading';
import ContextMenu from '@/components/ui/ContextMenu';
import { MenuItem, MenuItemLabel } from '@/components/gluestack/menu';
import { router } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { useCommunity } from '@/hooks/useCommunity';
import { useAuth } from '@/components/providers/AuthProvider';
import { MessageView } from '@/components/ui/MessageView';
import { AlertCircleIcon } from '@hugeicons-pro/core-stroke-rounded';
import { PlanCardWeb } from '@/components/domain/account/PlanCardWeb';
import { PremiumFeatures } from '@/components/domain/account/PremiumFeatures';
import { BillingFaq } from '@/components/domain/account/BillingFaq';
import { BillingLinksList, useBillingLinks } from '@/components/domain/account/BillingLinks';
import { CCBillPayments } from '@/components/domain/account/CCBillPayments';
import { PremiumStatusCard } from '@/components/domain/account/PremiumStatusCard';
import { useSubscribeInfo } from '@/hooks/useSubscribeInfo';
import { useRegisterRightPanelSlot } from '@/components/layout/RightPanelSlotContext';
import { useIsRightPanelVisible } from '@/components/layout/LargeScreenRightPanel';

export default function PlansScreen() {
  const { t } = useTranslation();
  const community = useCommunity();
  const { accountStatus } = useAuth();

  const { data: subscribeInfo, isPending: isLoading, error } = useSubscribeInfo();

  const billingLinks = useBillingLinks();

  const isRightPanelVisible = useIsRightPanelVisible();
  // Only alongside the plans themselves, not next to the loading, error or billing-unavailable states
  const showRightPanel =
    !!subscribeInfo?.BillingEnabled && !!subscribeInfo.Providers?.CCBill?.Plans;
  const rightPanelContent = useMemo(
    () =>
      showRightPanel ? (
        <VStack space="3xl">
          <BillingLinksList />
          <View>
            <Text className="mb-4 text-sm font-bold uppercase text-tertiary-500">
              {t('account.plans.ccbillPayments')}
            </Text>
            <CCBillPayments lastSubscriptionId={subscribeInfo?.LastCCBillSubscriptionId} />
          </View>
          <View>
            <Text className="mb-4 text-sm font-bold uppercase text-tertiary-500">
              {t('account.plans.billingFaq')}
            </Text>
            <BillingFaq />
          </View>
        </VStack>
      ) : null,
    [showRightPanel, t, subscribeInfo?.LastCCBillSubscriptionId]
  );
  useRegisterRightPanelSlot(rightPanelContent);

  const handleSubscribe = (planCode: number) => {
    if (!subscribeInfo?.Providers?.CCBill || !accountStatus?.MemberId) return;

    const ccbill = subscribeInfo.Providers.CCBill;
    const flexId = ccbill.FlexId;
    const subacc = ccbill.SubAccount;
    const memberId = accountStatus.MemberId;

    const url = `https://api.ccbill.com/wap-frontflex/flexforms/${flexId}?clientSubacc=${subacc}&subscriptionTypeId=${planCode}&currencyCode=840&memberId=${memberId}`;

    if (typeof window !== 'undefined') {
      window.open(url, '_blank');
    } else {
      Linking.openURL(url);
    }
  };

  const calculateSavings = (planPrice: number, monthlyPrice: number, months: number): number => {
    return Math.round((1 - planPrice / (monthlyPrice * months)) * 100);
  };

  if (isLoading) {
    return (
      <View className="flex-1">
        <ScreenHeader
          options={{
            headerShown: true,
            title: t('account.plans.title'),
          }}
        />
        <MessageView type="info" text={t('common.loading')}>
          <ActivityIndicator size="large" color="grey" />
        </MessageView>
      </View>
    );
  }

  if (error || !subscribeInfo) {
    return (
      <View className="flex-1">
        <ScreenHeader
          options={{
            headerShown: true,
            title: t('account.plans.title'),
          }}
        />
        <MessageView
          type="error"
          text={error?.message || 'Failed to load subscription information'}
          icon={AlertCircleIcon}
        />
      </View>
    );
  }

  if (!subscribeInfo.BillingEnabled) {
    return (
      <View className="flex-1">
        <ScreenHeader
          options={{
            headerShown: true,
            title: t('account.plans.title'),
          }}
        />
        <MessageView
          type="info"
          text={t('account.plans.billingNotAvailable')}
          icon={AlertCircleIcon}
        />
      </View>
    );
  }

  const ccbillProvider = subscribeInfo.Providers?.CCBill;
  const plans = ccbillProvider?.Plans;

  if (!ccbillProvider || !plans) {
    return (
      <View className="flex-1">
        <ScreenHeader
          options={{
            headerShown: true,
            title: t('account.plans.title'),
          }}
        />
        <MessageView type="error" text="Plan configuration not available" icon={AlertCircleIcon} />
      </View>
    );
  }

  const monthlyPrice = plans.Monthly.Price;
  const quarterlySavings = calculateSavings(plans.Quarterly.Price, monthlyPrice, 3);
  const yearlySavings = calculateSavings(plans.Yearly.Price, monthlyPrice, 12);

  return (
    <View className="flex-1">
      <ScreenHeader
        options={{
          headerShown: true,
          title: t('account.plans.title'),
          headerRight: () =>
            isRightPanelVisible ? null : (
              <ContextMenu>
                {billingLinks.map((link) => (
                  <MenuItem
                    key={link.href}
                    textValue={link.label}
                    onPress={() => router.push(link.href)}>
                    <MenuItemLabel>{link.label}</MenuItemLabel>
                  </MenuItem>
                ))}
              </ContextMenu>
            ),
        }}
      />

      <ScrollView className="bg-background-page">
        <VStack className="mx-auto w-full max-w-4xl p-4" space="4xl">
          {/* Premium Status Card (if user is premium): status first, ahead of features and plans */}
          {subscribeInfo.IsPremium && subscribeInfo.PremiumEndsOnUtc && community?.premiumPlan && (
            <PremiumStatusCard
              premiumEndsOnUtc={subscribeInfo.PremiumEndsOnUtc}
              premiumPlanName={community.premiumPlan}
            />
          )}

          {/* Premium Features Section */}
          <PremiumFeatures />

          {/* Plans Selection */}
          <VStack space="md">
            <VStack space="xs">
              <Heading size="md" className="ml-1 text-typography-700">
                {t('account.plans.choosePlan')}
              </Heading>
              {subscribeInfo.IsOldPricingEligible && (
                <Text size="sm" className="ml-1 text-success-600">
                  {t('account.plans.grandfatheredPricing')}
                </Text>
              )}
            </VStack>

            <View className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Monthly Plan */}
              <PlanCardWeb
                title={t('account.plans.oneMonth')}
                price={plans.Monthly.Price}
                period={t('account.plans.perMonth')}
                renewalDays={30}
                onSubscribe={() => handleSubscribe(plans.Monthly.Code)}
                isPremium={subscribeInfo.IsPremium}
              />

              {/* Quarterly Plan */}
              <PlanCardWeb
                title={t('account.plans.threeMonths')}
                price={plans.Quarterly.Price}
                period={t('account.plans.every3Months')}
                renewalDays={90}
                savings={t('account.plans.save', { percent: quarterlySavings })}
                onSubscribe={() => handleSubscribe(plans.Quarterly.Code)}
                isPremium={subscribeInfo.IsPremium}
              />

              {/* Yearly Plan */}
              <PlanCardWeb
                title={t('account.plans.oneYear')}
                price={plans.Yearly.Price}
                period={t('account.plans.perYear')}
                renewalDays={365}
                savings={t('account.plans.save', { percent: yearlySavings })}
                onSubscribe={() => handleSubscribe(plans.Yearly.Code)}
                isPremium={subscribeInfo.IsPremium}
              />

              {/* Combo Plan */}
              {plans.Combo && (
                <PlanCardWeb
                  title={t('account.plans.oneYearCombo')}
                  subTitle={t('account.plans.comboSubtitle')}
                  price={plans.Combo?.Price ?? 0}
                  period={t('account.plans.perYear')}
                  renewalDays={365}
                  onSubscribe={() => handleSubscribe(plans.Combo?.Code ?? 0)}
                  isPremium={subscribeInfo.IsPremium}
                  className="lg:col-span-2"
                />
              )}
            </View>
          </VStack>

          {/* CCBill Management Section (in the right panel when it's shown) */}
          {!isRightPanelVisible && (
            <VStack space="md">
              <Heading size="md" className="ml-1 text-typography-700">
                {t('account.plans.ccbillPayments')}
              </Heading>
              <Card size="md">
                <CCBillPayments lastSubscriptionId={subscribeInfo.LastCCBillSubscriptionId} />
              </Card>
            </VStack>
          )}

          {/* Billing FAQ Section (in the right panel when it's shown) */}
          {!isRightPanelVisible && (
            <VStack space="md">
              <Heading size="md" className="ml-1 text-typography-700">
                {t('account.plans.billingFaq')}
              </Heading>
              <Card size="md">
                <BillingFaq />
              </Card>
            </VStack>
          )}
        </VStack>
      </ScrollView>
    </View>
  );
}
